<template>
  <div v-loading="loading" class="meeting-content">
    <el-page-header :content="`${meetingName}（${meetingCode}）内容编辑`" @back="goBack" />

    <el-tabs v-model="activeTab" class="content-tabs" @tab-change="onTabChange">
      <!-- 基本信息 -->
      <el-tab-pane label="基本信息" name="basic">
        <el-form ref="basicFormRef" label-width="120px" :model="basicForm" style="max-width: 720px">
          <el-form-item label="会议名称"><el-input v-model.trim="basicForm.name" /></el-form-item>
          <el-form-item label="开始时间">
            <el-date-picker v-model="basicForm.startTime" type="datetime" value-format="YYYY-MM-DD HH:mm:ss" />
          </el-form-item>
          <el-form-item label="结束时间">
            <el-date-picker v-model="basicForm.endTime" type="datetime" value-format="YYYY-MM-DD HH:mm:ss" />
          </el-form-item>
          <!-- 场馆编码：场馆是独立维护的，需先编辑场馆再关联，暂不在本页编辑 -->
          <el-form-item label="图文介绍">
            <div class="intro-editor">
              <el-input v-model="intro.remark" placeholder="图文介绍文本" :rows="4" type="textarea" />
              <div class="intro-images">
                <div class="intro-images-title">配图</div>
                <el-row :gutter="10">
                  <el-col v-for="(img, i) in intro.fileUrl" :key="i" :lg="6" :md="8" :sm="12" :xs="24">
                    <div class="intro-img-item">
                      <el-image class="intro-img" fit="cover" :preview-src-list="[img]" :src="img" />
                      <el-button link type="danger" @click="intro.fileUrl.splice(i, 1)">删除</el-button>
                    </div>
                  </el-col>
                </el-row>
                <el-upload accept="image/*" :http-request="uploadIntroImage" multiple :show-file-list="false">
                  <el-button size="small">添加配图</el-button>
                </el-upload>
              </div>
              <div class="intro-links">
                <div class="intro-images-title">链接</div>
                <div v-for="(lk, i) in intro.linkList" :key="i" class="intro-link-row">
                  <el-input v-model="lk.linkUrl" placeholder="链接地址" />
                  <el-button link type="danger" @click="intro.linkList.splice(i, 1)">删除</el-button>
                </div>
                <el-button size="small" @click="intro.linkList.push({ linkUrl: '' })">添加链接</el-button>
              </div>
              <el-form-item label="链接文案" label-width="80px" style="margin-top: 10px">
                <el-input v-model="intro.linkContent" placeholder="链接文案" />
              </el-form-item>
            </div>
          </el-form-item>
          <el-form-item label="会议Logo">
            <div class="up-item">
              <el-image v-if="basicForm.logo" class="up-preview" fit="cover" :src="basicForm.logo" />
              <el-upload accept="image/*" :http-request="uploadLogo" :show-file-list="false">
                <el-button>上传图片</el-button>
              </el-upload>
              <el-button v-if="basicForm.logo" link type="danger" @click="basicForm.logo = ''">清除</el-button>
            </div>
          </el-form-item>
          <el-form-item label="分享封面">
            <div class="up-item">
              <el-image v-if="basicForm.shareCover" class="up-preview" fit="cover" :src="basicForm.shareCover" />
              <el-upload accept="image/*" :http-request="uploadShareCover" :show-file-list="false">
                <el-button>上传图片</el-button>
              </el-upload>
              <el-button v-if="basicForm.shareCover" link type="danger" @click="basicForm.shareCover = ''">
                清除
              </el-button>
            </div>
          </el-form-item>
          <el-form-item label="备注">
            <el-input v-model.trim="basicForm.remark" :rows="2" type="textarea" />
          </el-form-item>
          <!-- 是否可见及之后的字段（报名/审核/主办/短信/规模）暂不编辑 -->
          <el-form-item>
            <el-button :loading="saving" type="primary" @click="saveBasic">保存基本信息</el-button>
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <!-- Banner -->
      <el-tab-pane label="Banner" name="banner">
        <div class="tab-toolbar">
          <el-upload accept="image/*" :http-request="uploadBanner" multiple :show-file-list="false">
            <el-button type="primary">添加Banner</el-button>
          </el-upload>
          <el-button :loading="saving" type="success" @click="saveBanners">保存Banner</el-button>
        </div>
        <el-row :gutter="16">
          <el-col v-for="(b, i) in bannerList" :key="i" :lg="6" :md="8" :sm="12" :xs="24">
            <el-card class="banner-card" shadow="hover">
              <el-image class="banner-img" fit="cover" :preview-src-list="[b.fileUrl]" :src="b.fileUrl" />
              <div class="banner-foot">
                <span v-if="b.bannerId" class="hint">已存</span>
                <el-button link type="danger" @click="removeBanner(i, b)">删除</el-button>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </el-tab-pane>

      <!-- 日程 -->
      <el-tab-pane label="日程" name="schedule">
        <div class="tab-toolbar">
          <el-button type="primary" @click="openScheduleDialog()">添加日程</el-button>
        </div>
        <el-table border :data="scheduleList">
          <el-table-column label="标题" prop="title" />
          <el-table-column label="类型" prop="scheduleType" width="100" />
          <el-table-column label="开始时间" prop="startTimeStr" width="180" />
          <el-table-column label="结束时间" prop="endTimeStr" width="180" />
          <el-table-column label="排序" prop="sort" width="80" />
          <el-table-column label="操作" width="220">
            <template #default="{ row, $index }">
              <el-button link type="primary" @click="openScheduleDialog(row)">编辑</el-button>
              <el-button :disabled="$index === 0" link type="primary" @click="moveSchedule($index, -1)">上移</el-button>
              <el-button
                :disabled="$index === scheduleList.length - 1"
                link
                type="primary"
                @click="moveSchedule($index, 1)"
              >
                下移
              </el-button>
              <el-button link type="danger" @click="delSchedule(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <el-dialog v-model="scheduleDialog" :title="scheduleForm.scheduleCode ? '编辑日程' : '添加日程'" width="560px">
          <el-form label-width="100px" :model="scheduleForm">
            <el-form-item label="标题"><el-input v-model.trim="scheduleForm.title" /></el-form-item>
            <el-form-item label="开始时间">
              <el-date-picker v-model="scheduleForm.startTime" type="datetime" value-format="YYYY-MM-DD HH:mm:ss" />
            </el-form-item>
            <el-form-item label="结束时间">
              <el-date-picker v-model="scheduleForm.endTime" type="datetime" value-format="YYYY-MM-DD HH:mm:ss" />
            </el-form-item>
            <el-form-item label="类型">
              <el-select v-model="scheduleForm.scheduleType">
                <el-option label="普通" value="NORMAL" />
                <el-option label="主题" value="THEME" />
                <el-option label="子议程" value="SUB" />
              </el-select>
            </el-form-item>
            <el-form-item label="描述">
              <el-input v-model="scheduleForm.description" :rows="3" type="textarea" />
            </el-form-item>
          </el-form>
          <template #footer>
            <el-button @click="scheduleDialog = false">取消</el-button>
            <el-button :loading="saving" type="primary" @click="saveSchedule">确定</el-button>
          </template>
        </el-dialog>
      </el-tab-pane>

      <!-- 嘉宾 -->
      <el-tab-pane label="嘉宾" name="guest">
        <div class="tab-toolbar">
          <el-select v-model="guestScheduleCode" placeholder="选择日程" style="width: 320px" @change="loadGuests">
            <el-option v-for="s in scheduleList" :key="s.scheduleCode" :label="s.title" :value="s.scheduleCode" />
          </el-select>
          <el-button :disabled="!guestScheduleCode" type="primary" @click="openGuestDialog()">添加嘉宾</el-button>
        </div>
        <el-table border :data="guestList">
          <el-table-column label="头像" width="80">
            <template #default="{ row }">
              <el-image v-if="row.avatar" class="guest-avatar" fit="cover" :src="row.avatar" />
              <span v-else>/</span>
            </template>
          </el-table-column>
          <el-table-column label="姓名" prop="name" />
          <el-table-column label="职位" prop="position" />
          <el-table-column label="机构" prop="organization" />
          <el-table-column label="标签" prop="tag" width="100" />
          <el-table-column label="操作" width="160">
            <template #default="{ row }">
              <el-button link type="primary" @click="openGuestDialog(row)">编辑</el-button>
              <el-button link type="danger" @click="delGuest(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <el-dialog v-model="guestDialog" :title="guestForm.guestCode ? '编辑嘉宾' : '添加嘉宾'" width="560px">
          <el-form label-width="100px" :model="guestForm">
            <el-form-item label="姓名"><el-input v-model.trim="guestForm.name" /></el-form-item>
            <el-form-item label="手机号"><el-input v-model.trim="guestForm.phone" /></el-form-item>
            <el-form-item label="职位"><el-input v-model.trim="guestForm.position" /></el-form-item>
            <el-form-item label="机构"><el-input v-model.trim="guestForm.organization" /></el-form-item>
            <el-form-item label="部门"><el-input v-model.trim="guestForm.department" /></el-form-item>
            <el-form-item label="标签"><el-input v-model.trim="guestForm.tag" /></el-form-item>
            <el-form-item label="类型"><el-input v-model.trim="guestForm.type" /></el-form-item>
            <el-form-item label="头像">
              <div class="up-item">
                <el-image v-if="guestForm.avatar" class="up-preview" fit="cover" :src="guestForm.avatar" />
                <el-upload
                  accept="image/*"
                  :http-request="(o) => uploadToUrl(o, 'avatar', guestForm)"
                  :show-file-list="false"
                >
                  <el-button>上传</el-button>
                </el-upload>
              </div>
            </el-form-item>
            <el-form-item label="简介">
              <el-input v-model="guestForm.introduction" :rows="3" type="textarea" />
            </el-form-item>
          </el-form>
          <template #footer>
            <el-button @click="guestDialog = false">取消</el-button>
            <el-button :loading="saving" type="primary" @click="saveGuest">确定</el-button>
          </template>
        </el-dialog>
      </el-tab-pane>

      <!-- 嘉宾文件 -->
      <el-tab-pane label="嘉宾文件" name="guestFile">
        <div class="tab-toolbar">
          <el-select v-model="gfScheduleCode" placeholder="选择日程" style="width: 240px" @change="onGfScheduleChange">
            <el-option v-for="s in scheduleList" :key="s.scheduleCode" :label="s.title" :value="s.scheduleCode" />
          </el-select>
          <el-select v-model="gfGuestCode" placeholder="选择嘉宾" style="width: 240px" @change="loadGuestFiles">
            <el-option v-for="g in gfGuestList" :key="g.guestCode" :label="g.name" :value="g.guestCode" />
          </el-select>
        </div>
        <el-table border :data="guestFileList">
          <el-table-column label="文件" prop="fileName" />
          <el-table-column label="公开时间" prop="overtTime" width="180" />
          <el-table-column label="显示" width="100">
            <template #default="{ row }">
              <el-switch :model-value="row.isShow" @change="(v) => toggleGuestFile(row, v)" />
            </template>
          </el-table-column>
        </el-table>
        <div v-if="gfGuestCode" class="tab-toolbar">
          <el-upload :http-request="uploadGuestFile" multiple :show-file-list="false">
            <el-button type="primary">添加嘉宾文件</el-button>
          </el-upload>
          <el-date-picker
            v-model="newGuestFileOvertTime"
            placeholder="公开时间"
            type="datetime"
            value-format="YYYY-MM-DD HH:mm:ss"
          />
          <el-button :loading="saving" type="success" @click="saveGuestFiles">保存</el-button>
        </div>
      </el-tab-pane>

      <!-- 预览 -->
      <el-tab-pane label="预览" name="preview">
        <div class="preview-box">
          <el-button type="primary" @click="openPreview">生成二维码预览</el-button>
          <p class="hint">扫码在手机查看真实会议页效果（含已保存的 banner/日程/嘉宾）。</p>
          <el-image v-if="posterUrl" class="poster-img" fit="contain" :src="posterUrl" />
        </div>
      </el-tab-pane>
    </el-tabs>

    <generate-wx-vue ref="generateWxRef" />
  </div>
</template>

<script>
  import generateWxVue from '@/components/generateWx/generateWx.vue'
  export default {
    name: 'MeetingContent',
    components: { generateWxVue },
  }
</script>

<script setup>
  import { ref, reactive } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { getCurrentInstance } from 'vue'
  import {
    meetisShelfOrUpdate,
    getMeetingDetailAdmin,
    delMeetingBannerAdmin,
    getMeetingPosterAdmin,
    getScheduleListAdmin,
    createOrUpdateScheduleAdmin,
    delScheduleAdmin,
    updateScheduleSortAdmin,
    getScheduleDetailAdmin,
    createGuestAdmin,
    updateGuestAdmin,
    delGuestAdmin,
    createOrUpdateGuestFileAdmin,
    updateGuestFileShowAdmin,
  } from '@/api/exhibitionManage'
  import { upLoadAttchments } from '@/api/active'

  const { proxy } = getCurrentInstance()
  const route = useRoute()
  const router = useRouter()
  const meetingCode = route.query.meetingCode
  const meetingName = route.query.meetingName

  const loading = ref(false)
  const saving = ref(false)
  const activeTab = ref('basic')
  const generateWxRef = ref(null)

  const basicForm = reactive({
    code: meetingCode,
    name: '',
    startTime: '',
    endTime: '',
    venueCode: '',
    introduction: '',
    logo: '',
    shareCover: '',
    remark: '',
    visibleStatus: 1,
    isOpenSignUp: false,
    isAutoAudit: false,
    hostName: '',
    hostLogo: '',
    hostContactInformation: '',
    smsSign: '',
    meetingSize: '',
    meetingBannerAttachIds: [],
  })

  const bannerList = ref([])
  const scheduleList = ref([])
  const guestList = ref([])
  const guestScheduleCode = ref('')

  // 图文介绍结构化（introduction 存为 JSON 字符串：{remark, fileUrl[], linkList[{linkUrl}], linkContent}）
  const intro = reactive({ remark: '', fileUrl: [], linkList: [], linkContent: '' })
  const parseIntro = (json) => {
    try {
      const o = JSON.parse(json || '{}')
      intro.remark = o.remark || ''
      intro.fileUrl = Array.isArray(o.fileUrl) ? o.fileUrl : []
      intro.linkList = Array.isArray(o.linkList) ? o.linkList : []
      intro.linkContent = o.linkContent || ''
    } catch (e) {
      intro.remark = json || ''
      intro.fileUrl = []
      intro.linkList = []
      intro.linkContent = ''
    }
  }
  const serializeIntro = () =>
    JSON.stringify({
      remark: intro.remark || '',
      fileUrl: intro.fileUrl || [],
      linkList: intro.linkList || [],
      linkContent: intro.linkContent || '',
    })

  // schedule dialog
  const scheduleDialog = ref(false)
  const scheduleForm = reactive({
    meetingCode,
    meetingType: 'HOLD_MEETING',
    title: '',
    startTime: '',
    endTime: '',
    description: '',
    scheduleType: 'NORMAL',
    themeCode: '',
    scheduleCode: '',
  })

  // guest dialog
  const guestDialog = ref(false)
  const guestForm = reactive({
    meetingCode,
    meetingType: 'HOLD_MEETING',
    scheduleCode: '',
    guestCode: '',
    name: '',
    phone: '',
    position: '',
    organization: '',
    department: '',
    avatar: '',
    introduction: '',
    tag: '',
    type: '',
    guestRemark: '',
    sort: 0,
  })

  // guest file
  const gfScheduleCode = ref('')
  const gfGuestCode = ref('')
  const gfGuestList = ref([])
  const guestFileList = ref([])
  const newGuestFileOvertTime = ref('')
  const pendingGuestFiles = ref([]) // {attachId, overtTime}
  const posterUrl = ref('')

  const goBack = () => router.go(-1)

  // ---- upload helper ----
  const uploadOnce = async (option) => {
    const fd = new FormData()
    fd.append('file', option.file)
    const { data } = await upLoadAttchments(fd)
    return data // {id, fileUrl, fileName, ...}
  }
  const uploadToUrl = async (option, field, target) => {
    const t = target || basicForm
    const d = await uploadOnce(option)
    t[field] = d.fileUrl
  }
  const uploadLogo = (o) => uploadToUrl(o, 'logo')
  const uploadShareCover = (o) => uploadToUrl(o, 'shareCover')
  const uploadIntroImage = async (option) => {
    const d = await uploadOnce(option)
    intro.fileUrl.push(d.fileUrl)
  }

  // ---- load ----
  const loadDetail = async () => {
    loading.value = true
    try {
      const { data } = await getMeetingDetailAdmin(meetingCode)
      Object.assign(basicForm, {
        name: data.name,
        startTime: data.startTime,
        endTime: data.endTime,
        venueCode: data.venueCode,
        introduction: data.introduction,
        logo: data.logo,
        shareCover: data.shareCover,
        remark: data.remark,
        visibleStatus: data.visibleStatus,
        isOpenSignUp: data.isOpenSignUp,
        isAutoAudit: data.isAutoAudit,
        hostName: data.hostName,
        hostLogo: data.hostLogo,
        hostContactInformation: data.hostContactInformation,
        smsSign: data.smsSign,
        meetingSize: data.meetingSize,
      })
      parseIntro(data.introduction)
      bannerList.value = (data.meetingBannerDtos || []).map((b) => ({
        bannerId: b.bannerId,
        attachId: b.attachId,
        fileUrl: b.fileUrl,
      }))
    } catch (e) {
      proxy.$baseMessage('加载会议详情失败', 'error', 'vab-hey-message-error')
    } finally {
      loading.value = false
    }
  }

  const loadSchedules = async () => {
    const { data } = await getScheduleListAdmin('HOLD_MEETING', meetingCode)
    scheduleList.value = data || []
  }

  // ---- basic save ----
  const saveBasic = async () => {
    saving.value = true
    try {
      basicForm.introduction = serializeIntro()
      await meetisShelfOrUpdate(basicForm)
      proxy.$baseMessage('保存成功', 'success', 'vab-hey-message-success')
    } catch (e) {
      proxy.$baseMessage('保存失败', 'error', 'vab-hey-message-error')
    } finally {
      saving.value = false
    }
  }

  // ---- banner ----
  const uploadBanner = async (option) => {
    const d = await uploadOnce(option)
    bannerList.value.push({ attachId: String(d.id), fileUrl: d.fileUrl })
  }
  const saveBanners = async () => {
    saving.value = true
    try {
      await meetisShelfOrUpdate({ code: meetingCode, meetingBannerAttachIds: bannerList.value.map((b) => b.attachId) })
      proxy.$baseMessage('保存成功', 'success', 'vab-hey-message-success')
      await loadDetail()
    } catch (e) {
      proxy.$baseMessage('保存失败', 'error', 'vab-hey-message-error')
    } finally {
      saving.value = false
    }
  }
  const removeBanner = async (index, b) => {
    if (b.bannerId) {
      try {
        await delMeetingBannerAdmin(b.bannerId, meetingCode)
      } catch (e) {
        proxy.$baseMessage('删除失败', 'error', 'vab-hey-message-error')
        return
      }
    }
    bannerList.value.splice(index, 1)
  }

  // ---- schedule ----
  const openScheduleDialog = (row) => {
    Object.assign(scheduleForm, {
      meetingCode,
      meetingType: 'HOLD_MEETING',
      title: row?.title || '',
      startTime: row?.startTime || '',
      endTime: row?.endTime || '',
      description: row?.description || '',
      scheduleType: row?.scheduleType || 'NORMAL',
      themeCode: row?.themeCode || '',
      scheduleCode: row?.scheduleCode || '',
    })
    scheduleDialog.value = true
  }
  const saveSchedule = async () => {
    if (!scheduleForm.title) {
      proxy.$baseMessage('请填写标题', 'warning', 'vab-hey-message-warning')
      return
    }
    saving.value = true
    try {
      await createOrUpdateScheduleAdmin(scheduleForm)
      proxy.$baseMessage('保存成功', 'success', 'vab-hey-message-success')
      scheduleDialog.value = false
      await loadSchedules()
    } catch (e) {
      proxy.$baseMessage('保存失败', 'error', 'vab-hey-message-error')
    } finally {
      saving.value = false
    }
  }
  const delSchedule = async (row) => {
    try {
      await proxy.$baseConfirm('确定删除该日程？', null, async () => {
        await delScheduleAdmin(row.scheduleCode)
        proxy.$baseMessage('删除成功', 'success', 'vab-hey-message-success')
        await loadSchedules()
      })
    } catch (e) {
      // cancel
    }
  }
  const moveSchedule = async (index, dir) => {
    const arr = [...scheduleList.value]
    const j = index + dir
    ;[arr[index], arr[j]] = [arr[j], arr[index]]
    scheduleList.value = arr
    try {
      await updateScheduleSortAdmin(arr.map((s, i) => ({ scheduleCode: s.scheduleCode, sort: i })))
    } catch (e) {
      await loadSchedules()
    }
  }

  // ---- guest ----
  const loadGuests = async () => {
    if (!guestScheduleCode.value) {
      guestList.value = []
      return
    }
    try {
      const { data } = await getScheduleDetailAdmin(guestScheduleCode.value)
      guestList.value = data?.guestInfoList || []
    } catch (e) {
      guestList.value = []
    }
  }
  const openGuestDialog = (row) => {
    Object.assign(guestForm, {
      meetingCode,
      meetingType: 'HOLD_MEETING',
      scheduleCode: guestScheduleCode.value,
      guestCode: row?.guestCode || '',
      name: row?.name || '',
      phone: row?.phone || '',
      position: row?.position || '',
      organization: row?.organization || '',
      department: row?.department || '',
      avatar: row?.avatar || '',
      introduction: row?.introduction || '',
      tag: row?.tag || '',
      type: row?.type || '',
      guestRemark: row?.guestRemark || '',
      sort: row?.sort || 0,
    })
    guestDialog.value = true
  }
  const saveGuest = async () => {
    if (!guestForm.name) {
      proxy.$baseMessage('请填写姓名', 'warning', 'vab-hey-message-warning')
      return
    }
    saving.value = true
    try {
      if (guestForm.guestCode) await updateGuestAdmin(guestForm)
      else await createGuestAdmin(guestForm)
      proxy.$baseMessage('保存成功', 'success', 'vab-hey-message-success')
      guestDialog.value = false
      await loadGuests()
    } catch (e) {
      proxy.$baseMessage('保存失败', 'error', 'vab-hey-message-error')
    } finally {
      saving.value = false
    }
  }
  const delGuest = async (row) => {
    try {
      await proxy.$baseConfirm('确定删除该嘉宾？', null, async () => {
        await delGuestAdmin(row.guestCode, guestScheduleCode.value)
        proxy.$baseMessage('删除成功', 'success', 'vab-hey-message-success')
        await loadGuests()
      })
    } catch (e) {
      // cancel
    }
  }

  // ---- guest file ----
  const onGfScheduleChange = async () => {
    gfGuestCode.value = ''
    gfGuestList.value = []
    guestFileList.value = []
    if (!gfScheduleCode.value) return
    try {
      const { data } = await getScheduleDetailAdmin(gfScheduleCode.value)
      gfGuestList.value = data?.guestInfoList || []
    } catch (e) {
      gfGuestList.value = []
    }
  }
  const loadGuestFiles = async () => {
    if (!gfGuestCode.value) {
      guestFileList.value = []
      return
    }
    const g = gfGuestList.value.find((x) => x.guestCode === gfGuestCode.value)
    guestFileList.value = (g?.guestFileInfoDtos || []).map((f) => ({
      fileCode: f.fileCode,
      fileName: f.fileName,
      fileUrl: f.fileUrl,
      overtTime: f.overtTime,
      isShow: f.isShow,
    }))
  }
  const uploadGuestFile = async (option) => {
    const d = await uploadOnce(option)
    pendingGuestFiles.value.push({
      attachId: String(d.id),
      overtTime: newGuestFileOvertTime.value,
      fileName: d.fileName,
    })
    guestFileList.value.push({
      fileName: d.fileName,
      fileUrl: d.fileUrl,
      overtTime: newGuestFileOvertTime.value,
      isShow: true,
    })
  }
  const saveGuestFiles = async () => {
    if (!pendingGuestFiles.value.length) return
    saving.value = true
    try {
      await createOrUpdateGuestFileAdmin({
        guestCode: gfGuestCode.value,
        meetingType: 'HOLD_MEETING',
        meetingCode,
        scheduleCode: gfScheduleCode.value,
        guestFileInfoList: pendingGuestFiles.value,
      })
      proxy.$baseMessage('保存成功', 'success', 'vab-hey-message-success')
      pendingGuestFiles.value = []
      await loadGuestFiles()
    } catch (e) {
      proxy.$baseMessage('保存失败', 'error', 'vab-hey-message-error')
    } finally {
      saving.value = false
    }
  }
  const toggleGuestFile = async (row, v) => {
    try {
      await updateGuestFileShowAdmin(row.fileCode, v)
      row.isShow = v
    } catch (e) {
      proxy.$baseMessage('操作失败', 'error', 'vab-hey-message-error')
    }
  }

  // ---- preview ----
  const openPreview = () => {
    const qrCodeParams = {
      autoColor: false,
      b: 0,
      env: '',
      g: 0,
      jumpPath: meetingCode,
      jumpType: '',
      logo: '',
      params: `{"meetingCode":"${meetingCode}"}`,
      path: 'pages/index/index',
      r: 0,
      scenesType: 'USER_HOLD_MEETING',
      width: 1280,
      miniPath: `subPackage/meet/holdMeet/detail?code=${meetingCode}`,
    }
    generateWxRef.value.handleOpen({ code: meetingCode, meetingCode, meetingName }, qrCodeParams)
  }

  const onTabChange = async (name) => {
    if (name === 'schedule' && !scheduleList.value.length) await loadSchedules()
    if (name === 'banner' && !bannerList.value.length) await loadDetail()
    if (name === 'preview') await loadPoster()
  }

  const loadPoster = async () => {
    try {
      const { data } = await getMeetingPosterAdmin(meetingCode)
      posterUrl.value = data?.coverUrl || data?.shareCover || data?.qrCodeUrl || ''
    } catch (e) {
      posterUrl.value = ''
    }
  }

  loadDetail()
  loadSchedules()
</script>

<style lang="scss" scoped>
  .meeting-content {
    padding: 16px;
  }

  .content-tabs {
    margin-top: 16px;
  }

  .tab-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .up-item {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .up-preview {
    width: 80px;
    height: 80px;
    border-radius: 4px;
    border: 1px solid #ebeef5;
  }

  .intro-editor {
    width: 100%;
  }

  .intro-images-title {
    margin: 10px 0 6px;
    font-size: 13px;
    color: #606266;
  }

  .intro-img-item {
    text-align: center;
    margin-bottom: 10px;
  }

  .intro-img {
    width: 100%;
    height: 100px;
    border-radius: 4px;
    border: 1px solid #ebeef5;
  }

  .intro-link-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
  }

  .banner-card {
    margin-bottom: 16px;

    .banner-img {
      width: 100%;
      height: 140px;
      border-radius: 4px;
    }

    .banner-foot {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 8px;
    }
  }

  .guest-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
  }

  .preview-box {
    text-align: center;
    padding: 24px;

    .hint {
      margin: 12px 0;
      color: #909399;
    }

    .poster-img {
      max-width: 320px;
      margin: 0 auto;
    }
  }

  .hint {
    color: #909399;
    font-size: 12px;
  }
</style>
